import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SendRounded from '@mui/icons-material/SendRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import { useTranslation } from 'react-i18next';
import {
  allowedKinds,
  type EmittableKind,
  type MembershipReadinessReport,
  type MembershipSubmissionSummary,
} from '@dosfilos/domain';
import { seminaryNetworkApi } from '../../../lib/api/network.api';
import { errorText } from '../../../lib/error-text';
import { PacketPreviewDialog } from './PacketPreviewDialog';
import { SubmitConfirmDialog } from './SubmitConfirmDialog';

// ─── Presentar a la red (R1-PR5) ────────────────────────────────────────────
//
// Hasta acá el TC podía preparar todo su expediente y no tenía forma de
// entregarlo: someter existía como callable desde R1-PR3 y ninguna pantalla lo
// llamaba. Las rondas de prueba se creaban por script.
//
// LO QUE ESTA SECCIÓN TIENE QUE LOGRAR, más allá del botón:
//
//  · QUE EL SUBMIT NO SEA A CIEGAS. Todo lo anterior al envío es privado y
//    reversible; el envío no. El TC ve el paquete EXACTO que va a cruzar —
//    construido con la misma función del dominio que usa el servidor, no una
//    aproximación de la UI— antes de decidir.
//  · QUE LA ELECCIÓN SEA CLARA. Consulta y solicitud son actos distintos y el
//    segundo arranca un plazo de dos años. El conjunto disponible lo acota el
//    servidor; acá se explica QUÉ significa cada uno y cuál está disponible.
//  · QUE VEA LO QUE LE RESPONDIERON. Las observaciones de rondas anteriores son
//    el canal de retorno de la red; sin verlas, re-someter es adivinar.

export function NetworkSubmissionSection({
  tenantId,
  networkTenantId,
  readiness,
  cycleYear,
}: {
  tenantId: string;
  /** Puntero de conveniencia del tenant. Su ausencia significa que nadie afilió
   *  a esta institución todavía; su presencia NO garantiza que la afiliación
   *  siga activa — eso lo decide la red y el submit lo verifica. */
  networkTenantId: string | undefined;
  readiness: MembershipReadinessReport | null;
  cycleYear: number;
}) {
  const { t } = useTranslation('membership');
  const [rounds, setRounds] = useState<MembershipSubmissionSummary[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirming, setConfirming] = useState<EmittableKind | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setRounds(await seminaryNetworkApi.listOwnSubmissions({ tenantId }));
    } catch (e) {
      setError(errorText(e));
      setRounds([]);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(kind: EmittableKind) {
    setBusy(true);
    setError('');
    try {
      await seminaryNetworkApi.submit({ tenantId, cycleYear, submissionKind: kind });
      setConfirming(null);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(submissionId: string) {
    setBusy(true);
    setError('');
    try {
      await seminaryNetworkApi.withdraw({ tenantId, submissionId });
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (!networkTenantId) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
        <SectionTitle t={t} />
        <Alert severity="info" variant="outlined">
          {t('network.notLinked')}
        </Alert>
      </Paper>
    );
  }

  if (rounds === null) {
    return (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
        <CircularProgress size={22} />
      </Paper>
    );
  }

  const open = rounds.find((r) => r.status === 'submitted') ?? null;
  const closed = rounds.filter((r) => r.status !== 'submitted').sort((a, b) => b.round - a.round);
  // El conjunto lo decide el servidor con la misma función; acá se anticipa para
  // no ofrecer un botón que va a ser rechazado.
  const allowed: EmittableKind[] = readiness
    ? allowedKinds(readiness.stage, readiness.overallStatus)
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
      <SectionTitle t={t} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Lo irreversible se dice ANTES, no en un tooltip después. */}
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {t('network.privateUntilSubmit')}
      </Alert>

      {open ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('network.openRoundTitle')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip
              size="small"
              color={open.submissionKind === 'formal' ? 'primary' : 'default'}
              variant={open.submissionKind === 'formal' ? 'filled' : 'outlined'}
              label={t(`network.kind.${open.submissionKind}`)}
            />
            <Typography variant="body2" color="text.secondary">
              {t('network.roundN', { round: open.round })} · {t(`stages.${open.stage}`)}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('network.awaitingReply')}
          </Typography>
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            disabled={busy}
            onClick={() => void withdraw(open.id)}
            sx={{ textTransform: 'none' }}
          >
            {t('network.withdraw')}
          </Button>
        </Box>
      ) : !readiness ? (
        <Alert severity="warning" variant="outlined">
          {t('network.needsAnalysis')}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<VisibilityRounded />}
            onClick={() => setPreview(true)}
            sx={{ textTransform: 'none' }}
          >
            {t('network.preview')}
          </Button>

          {allowed.includes('formal') ? (
            <Button
              variant="contained"
              startIcon={<SendRounded />}
              disabled={busy}
              onClick={() => setConfirming('formal')}
              sx={{ textTransform: 'none' }}
            >
              {t('network.submitFormal')}
            </Button>
          ) : (
            <Alert severity="info" variant="outlined">
              {t('network.formalUnavailable')}
            </Alert>
          )}

          <Button
            variant={allowed.includes('formal') ? 'outlined' : 'contained'}
            startIcon={<SendRounded />}
            disabled={busy}
            onClick={() => setConfirming('advisory')}
            sx={{ textTransform: 'none' }}
          >
            {t('network.submitAdvisory')}
          </Button>
        </Stack>
      )}

      {closed.length > 0 && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('network.historyTitle')}
          </Typography>
          <Stack divider={<Divider />}>
            {closed.map((r) => (
              <Box key={r.id} sx={{ py: 1.25 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('network.roundN', { round: r.round })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`stages.${r.stage}`)}
                  </Typography>
                  <Chip size="small" variant="outlined" label={t(`network.status.${r.status}`)} />
                </Stack>
                {/* LO QUE DIJO LA PLATAFORMA. Se traduce acá, al leer, con el
                    escalón resuelto a su rótulo — por eso `systemNote` guarda el
                    identificador y no el texto. */}
                {r.systemNote && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, fontStyle: 'italic' }}
                  >
                    {t(`network.systemNote.${r.systemNote.code}`, {
                      ...r.systemNote.params,
                      stage: r.systemNote.params?.['stage']
                        ? t(`stages.${r.systemNote.params['stage']}`)
                        : '',
                    })}
                    {r.systemNote.params?.['reason'] ? (
                      <Box component="span" sx={{ display: 'block' }}>
                        {t('network.systemNote.reason', {
                          reason: r.systemNote.params['reason'],
                        })}
                      </Box>
                    ) : null}
                  </Typography>
                )}
                {/* LAS OBSERVACIONES SON EL CANAL DE RETORNO Y LA VOZ DEL REVISOR.
                    Se muestran enteras y sin tocar: resumirlas dejaría al TC
                    adivinando qué corregir. */}
                {r.observations && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                  >
                    {r.observations}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}

      {preview && readiness && (
        <PacketPreviewDialog report={readiness} onClose={() => setPreview(false)} />
      )}
      {confirming && readiness && (
        <SubmitConfirmDialog
          kind={confirming}
          stage={readiness.stage}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void submit(confirming)}
        />
      )}
    </Paper>
  );
}

function SectionTitle({ t }: { t: (k: string) => string }) {
  return (
    <>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1, fontWeight: 700 }}
      >
        {t('network.sectionTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5} mb={2}>
        {t('network.sectionBody')}
      </Typography>
    </>
  );
}
